/**
 * App-wide semantic color convention (BudgetOS).
 *
 * Green  = positive / good / under spend target / savings on or ahead
 * Red    = negative / alert / over spend target / debt pressure
 * Amber  = neutral informational / in-progress / near target
 * Neutral gray/beige = purely informational, no judgment
 *
 * Special case — Savings categories & 50/30/20 Savings bucket:
 * Exceeding the savings target is GOOD (green), not an overspend alert.
 * Needs/Wants exceeding target is BAD (red).
 */

export type SemanticTone = "positive" | "negative" | "caution" | "neutral";

export const SEMANTIC = {
  positive: {
    text: "text-emerald-600 dark:text-emerald-400",
    bar: "#22c55e",
    kpi: "mint" as const,
  },
  negative: {
    text: "text-rose-600 dark:text-rose-400",
    bar: "#ef4444",
    kpi: "coral" as const,
  },
  caution: {
    text: "text-amber-600 dark:text-amber-400",
    bar: "#f59e0b",
    kpi: "lemon" as const,
  },
  neutral: {
    text: "text-muted-foreground",
    bar: "hsl(var(--muted-foreground))",
    kpi: "lavender" as const,
  },
} as const;

/** Expense budget line: over target = bad; savings group over = good */
export function budgetLineTone(
  group: string,
  actual: number,
  target: number
): SemanticTone {
  if (target <= 0) return "neutral";
  const over = actual > target;
  if (group === "savings") {
    if (over || actual >= target) return "positive";
    if (actual / target >= 0.8) return "caution";
    return "neutral";
  }
  if (over) return "negative";
  if (actual / target >= 0.9) return "caution";
  return "positive";
}

/** 50/30/20: savings over target is success; needs/wants over is alert */
export function rule503020Tone(
  kind: "needs" | "wants" | "savings",
  actualPct: number,
  targetPct: number
): { tone: SemanticTone; labelKey: string } {
  const over = actualPct > targetPct + 5;
  const under = actualPct < targetPct - 5;
  if (kind === "savings") {
    if (over || actualPct >= targetPct - 5) {
      return { tone: "positive", labelKey: over ? "rule.status.ahead" : "rule.status.onTrack" };
    }
    return { tone: "caution", labelKey: "rule.status.below" };
  }
  if (over) return { tone: "negative", labelKey: "rule.status.over" };
  if (under) return { tone: "positive", labelKey: "rule.status.under" };
  return { tone: "positive", labelKey: "rule.status.onTrack" };
}

export function leftoverTone(leftover: number): SemanticTone {
  if (leftover > 0) return "positive";
  if (leftover < 0) return "negative";
  return "neutral";
}

export function savingsRateTone(rate: number, target = 20): SemanticTone {
  if (rate >= target) return "positive";
  if (rate >= target * 0.5) return "caution";
  return "negative";
}
